import { useConnectGoogleCalendarMutation } from '@/api/piloteer';
import calendarPermissionImage from '@/assets/gifs/calendar.gif';
import { Spinner } from '@/ui/@components/feedback/loading/spinner';
import { Button } from '@/ui/@components/primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/@components/primitives/dialog';
import { APP_ROUTES } from '@/ui/application-routes';
import { useToast } from '@/ui/hooks/use-toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Module-level flag to prevent duplicate requests in StrictMode
let isProcessing = false;

const GoogleCalendarOAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isConnecting = useRef(false);
  const mounted = useRef(true);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);

  const [connectGoogleCalendar] = useConnectGoogleCalendarMutation();

  const handleTryAgain = () => {
    setShowPermissionDialog(false);
    navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, {
      state: {
        googleCalendarConnectError: true,
        errorDetails: 'Calendar access permission required',
      },
      replace: true,
    });
  };

  const handleConnectGoogleCalendar = useCallback(
    async (authorizationCode: string) => {
      // Prevent duplicate requests using module-level flag
      if (isProcessing || isConnecting.current) {
        console.log('Skipping duplicate request');
        return;
      }
      isProcessing = true;
      isConnecting.current = true;

      // Check if scope parameter exists and includes calendar access
      const scope = searchParams.get('scope');
      if (
        !scope ||
        !scope.includes('https://www.googleapis.com/auth/calendar')
      ) {
        console.error('Calendar permission not granted');
        setShowPermissionDialog(true);
        isProcessing = false;
        isConnecting.current = false;
        return;
      }

      try {
        const state = searchParams.get('state');
        if (!state)
          throw new Error('Missing authorization state. Please connect again.');
        await connectGoogleCalendar({
          authorizationCode,
          state,
        }).unwrap();

        console.log('Google Calendar connection successful');

        toast({
          title: 'Google Calendar connected',
          description: 'Your calendar events will sync automatically.',
          variant: 'success',
        });

        // Always navigate on success, regardless of mounted state
        setTimeout(() => {
          console.log('Navigating to integrations page...');
          navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, {
            state: { googleCalendarConnected: true },
            replace: true,
          });
        }, 500);
      } catch (error: unknown) {
        const err = error as {
          status?: number;
          data?: { message?: string };
          message?: string;
          stack?: string;
        };
        console.error('Detailed error:', {
          error,
          status: err.status,
          data: err.data,
          message: err.message,
          stack: err.stack,
        });

        // Handle specific error cases
        let toastTitle = 'Failed to connect Google Calendar';
        let toastDescription =
          err.data?.message ||
          err.message ||
          'Please try again or contact support';

        if (err.status === 400) {
          toastTitle = 'Invalid or expired authorization';
          toastDescription =
            'The authorization code may have expired or already been used. Please try connecting again.';
        }

        toast({
          title: toastTitle,
          description: toastDescription,
          variant: 'destructive',
        });

        // Always navigate on error, regardless of mounted state
        navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, {
          state: {
            googleCalendarConnectError: true,
            errorDetails: err.data?.message || err.message,
          },
          replace: true,
        });
      } finally {
        isConnecting.current = false;
        // Reset the module-level flag after a delay to allow retries
        setTimeout(() => {
          isProcessing = false;
        }, 3000);
      }
    },
    [connectGoogleCalendar, navigate, toast, searchParams]
  );

  useEffect(() => {
    const authorizationCode = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Clear URL parameters to prevent reuse
    if (authorizationCode || error) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check for OAuth errors
    if (error) {
      console.error('OAuth error received:', { error, errorDescription });

      toast({
        title: 'Authorization failed',
        description: errorDescription || error,
        variant: 'destructive',
      });

      setTimeout(() => {
        navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, {
          state: {
            googleCalendarConnectError: true,
            errorDetails: errorDescription || error,
          },
          replace: true,
        });
      }, 2000);
      return;
    }

    if (authorizationCode) {
      handleConnectGoogleCalendar(authorizationCode);
    } else {
      console.warn('No authorization code found in URL');
      navigate(APP_ROUTES.SETTINGS.INTEGRATIONS, {
        state: {
          googleCalendarConnectError: true,
          errorDetails: 'No authorization code received',
        },
        replace: true,
      });
    }

    return () => {
      console.log('GoogleCalendarOAuthCallback unmounting');
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to ensure this only runs once

  return (
    <>
      <Dialog
        open={showPermissionDialog}
        onOpenChange={setShowPermissionDialog}
      >
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Calendar Access Required</DialogTitle>
            <DialogDescription>
              Please check the box to allow Piloteer to access your Google
              Calendar.
            </DialogDescription>
          </DialogHeader>
          <div className='my-4'>
            <img
              src={calendarPermissionImage}
              alt='Google Calendar Permission Checkbox'
              className='w-full rounded-lg border border-border'
            />
          </div>
          <DialogFooter>
            <Button onClick={handleTryAgain}>Try Again</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className='flex items-center justify-center h-screen'>
        <div className='text-center'>
          <Spinner />
          <p className='mt-4 text-xl font-medium'>
            Connecting your Google Calendar
          </p>
          <p className='mt-2 text-muted-foreground'>
            Please wait while we complete the setup...
          </p>
        </div>
      </div>
    </>
  );
};

export default GoogleCalendarOAuthCallback;
